'use client';

import {
  Box,
  Button,
  ClickAwayListener,
  Fade,
  FormControl,
  FormControlLabel,
  FormLabel,
  Paper,
  Popper,
  Select,
  Stack,
  Switch,
  Tooltip,
  Typography,
  MenuItem as MuiMenuItem,
  type IconProps,
} from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';
import classNames from 'classnames';
import React, { useContext, useState } from 'react';

import { AiStarBig } from '@/components/assistant-ui/assets/aiStarBig';
import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { Icon } from '@/components/shared/icon';
import { Link } from '@/components/shared/link';
import { ThemeContext } from '@/components/theme';
import { useEnvironment } from '@/contexts/useEnvironment';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { CloudProviders } from '@/data/inventory/data';
import { getDocsUrl } from '@/docs/docsUrls';

export const ASSISTANT_MARGIN_BOTTOM = 24;
export const ASSISTANT_HEIGHT = 70;

export const AssistantMenuItem = ({
  href,
  title,
  selected,
}: {
  title: string;
  href: string;
  selected?: boolean;
}) => {
  return (
    <Stack
      height={`${ASSISTANT_HEIGHT}px`}
      className={`mb-[${ASSISTANT_MARGIN_BOTTOM}px]`}
      gap='4px'
    >
      <Box
        sx={{
          background: 'var(--mui-palette-custom-GRADIENT-AI)',
          height: '1px',
          width: '100%',
        }}
      />
      <Link
        href={href}
        className={classNames('slide-left h-[60px] min-h-[60px]', {
          'bg-white': selected,
          'text-[var(--mui-palette-custom-BLACK-01)]': selected,
          'hover:bg-[var(--mui-palette-custom-BLACK-02)]': !selected,
        })}
      >
        <Box className='w-[24px] h-[24px] ml-[14px] mt-[-8px]'>
          <AiStarBig />
        </Box>
        <Typography
          className={classNames(
            'slide-left collapse absolute left-[60px] w-[200px] opacity-0',
            {
              'text-[var(--mui-palette-custom-BLACK-01)]': selected,
              'text-white': !selected,
            }
          )}
        >
          {title}
        </Typography>
      </Link>
      <Box
        sx={{
          background: 'var(--mui-palette-custom-GRADIENT-AI)',
          height: '1px',
          width: '100%',
        }}
      />
    </Stack>
  );
};

export const MenuItem = ({
  href,
  title,
  iconClass,
  testId,
  disabled,
  onClick,
  selected,
  variant = 'sidebar',
}: {
  title: string;
  href: string;
  iconClass: string;
  testId?: string;
  disabled?: boolean;
  onClick?: () => void;
  selected?: boolean;
  variant?: 'sidebar' | 'dropdown';
}) => {
  const isDropdown = variant === 'dropdown';

  return (
    <Link
      href={href}
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      className={classNames({
        'slide-left h-[60px] min-h-[60px]': !isDropdown,
        'h-[48px] flex items-center px-[16px]': isDropdown,
        'bg-white': selected,
        'text-[var(--mui-palette-custom-BLACK-01)]': selected,
        'hover:bg-[var(--mui-palette-custom-BLACK-02)]': !selected && !disabled,
        'cursor-default': disabled,
      })}
    >
      <i
        className={classNames(`${iconClass} w-[24px] h-[24px]`, {
          'ml-[18px]': !isDropdown,
          'mr-[12px]': isDropdown,
          'text-[var(--mui-palette-custom-BLACK-01)]': isDropdown && selected,
          'text-white': isDropdown && !selected && !disabled,
          'text-[var(--mui-palette-custom-GREY-01)]': disabled,
        })}
      />
      <Typography
        className={classNames({
          'slide-left collapse absolute left-[60px] w-[200px] opacity-0':
            !isDropdown,
          'text-[var(--mui-palette-custom-BLACK-01)]': selected,
          'text-white': !selected && !disabled,
          'text-[var(--mui-palette-custom-GREY-01)]': disabled,
        })}
      >
        {title}
      </Typography>
    </Link>
  );
};

interface ActionIconProps extends IconProps {
  title: string;
  isActive: boolean;
  iconClass: string;
  onClick: () => void;
}

const ActionIcon = ({
  title,
  isActive,
  iconClass,
  onClick,
  ...rest
}: ActionIconProps) => {
  return (
    <Tooltip title={title} PopperProps={{ disablePortal: true }}>
      <Icon
        {...rest}
        sx={{
          backgroundColor: isActive
            ? 'white'
            : 'var(--mui-palette-custom-BLACK-01)',

          '&:hover': {
            backgroundColor: isActive
              ? 'white'
              : 'var(--mui-palette-custom-BLACK-02)',
          },

          '& i': {
            color: isActive ? 'var(--mui-palette-custom-BLACK-01)' : 'white',
          },
        }}
        iconClass={iconClass}
        isActive={isActive}
        onClick={onClick}
      />
    </Tooltip>
  );
};

export const NavigationActions = ({
  setForceOpen,
}: {
  setForceOpen: (open: boolean) => void;
}) => {
  const { theme, changeTheme } = useContext(ThemeContext);
  const isDarkMode = theme === 'dark';
  const { isDemo, isDev } = useEnvironment();

  return (
    <Box className='navigation-actions slide-left collapse opacity-0 absolute left-[24px] right-[24px] bottom-[12px] '>
      <Stack
        direction='row'
        className='w-[257px]'
        alignItems='center'
        justifyContent='space-between'
      >
        <Link href={getDocsUrl('root')} target='_blank' className='w-auto'>
          <ButtonWithIcon
            text='Eon Docs'
            data-testid='eon-docs'
            variant='outlined'
            icon='material-symbols-help-outline-rounded'
            sx={{
              borderColor: 'white',
              background: 'transparent',
              color: 'white',

              '&:hover': {
                color: 'white !important',
                backgroundColor: `var(--mui-palette-custom-BLACK-02) !important`,
              },
            }}
          />
        </Link>
        {(isDemo || isDev) && <DemoSettingsMenu setForceOpen={setForceOpen} />}
        <Stack direction='row' gap='12px'>
          <ActionIcon
            data-testid='light-mode'
            title='Light mode'
            isActive={!isDarkMode}
            iconClass='material-symbols-light-mode-outline-rounded'
            onClick={() => changeTheme('light')}
          />
          <ActionIcon
            data-testid='dark-mode'
            title='Dark mode'
            isActive={isDarkMode}
            iconClass='material-symbols-bedtime-outline-rounded'
            onClick={() => changeTheme('dark')}
          />
        </Stack>
      </Stack>
    </Box>
  );
};

export interface OverflowMenuItem {
  key: string;
  href: string;
  title: string;
  iconClass: string;
  disabled?: boolean;
  isAssistant?: boolean;
}

export const MoreMenuItem = ({
  items,
  setForceOpen,
}: {
  items: OverflowMenuItem[];
  setForceOpen: (open: boolean) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleClose = () => {
    setOpen(false);
    setForceOpen(false);
  };

  if (items.length === 0) return null;

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box>
        <Box
          ref={setAnchorEl}
          onClick={(event) => {
            event.stopPropagation();
            event.preventDefault();
            setOpen(!open);
            setForceOpen(!open);
          }}
          className='slide-left h-[60px] min-h-[60px] cursor-pointer flex items-center hover:bg-[var(--mui-palette-custom-BLACK-02)]'
          sx={{
            '& i': { color: 'white' },
          }}
        >
          <i className='material-symbols-more-horiz w-[24px] h-[24px] ml-[18px]' />
          <Typography className='slide-left collapse absolute left-[60px] w-[200px] opacity-0 text-white'>
            More
          </Typography>
        </Box>
        <Popper
          open={open}
          anchorEl={anchorEl}
          placement='right-start'
          transition
          sx={{ zIndex: 1001 }}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps}>
              <Paper
                sx={{
                  boxShadow: 'var(--mui-customShadows-lg)',
                  backgroundColor: 'var(--mui-palette-custom-BLACK-01)',
                  minWidth: '200px',
                  py: 1,
                }}
              >
                <Stack>
                  {items.map((item) => (
                    <MenuItem
                      key={item.key}
                      href={item.href}
                      iconClass={item.iconClass}
                      title={item.title}
                      disabled={item.disabled}
                      onClick={handleClose}
                      variant='dropdown'
                    />
                  ))}
                </Stack>
              </Paper>
            </Fade>
          )}
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

const DemoSettingsMenu = ({
  setForceOpen,
}: {
  setForceOpen: (open: boolean) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { demoSettings, updateDemoSettings } = useEnvironment();
  const { assistantEnabled } = useFeatureFlags();

  return (
    <>
      <Tooltip title='Console Settings'>
        <Icon
          ref={setAnchorEl}
          onClick={(event) => {
            event.stopPropagation();
            setOpen(!open);
            setForceOpen(!open);
          }}
          iconClass='material-symbols-settings-outline-rounded'
          sx={{
            cursor: 'pointer',
            backgroundColor: 'var(--mui-palette-custom-BLACK-01)',

            '&:hover': {
              backgroundColor: 'var(--mui-palette-custom-BLACK-02)',
            },

            '& i': {
              color: 'white',
            },
          }}
        />
      </Tooltip>
      <Popper
        open={open}
        anchorEl={anchorEl}
        transition
        disablePortal
        sx={{ zIndex: 5 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps}>
            <Paper
              sx={{
                boxShadow: 'var(--mui-customShadows-lg)',
              }}
            >
              <Stack
                onClick={(event) => event.stopPropagation()}
                sx={{
                  width: '250px',
                  padding: '24px',
                }}
                gap='24px'
              >
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='space-between'
                >
                  <Typography variant='h3'>Console Settings</Typography>
                  <Icon
                    iconClass='material-symbols-close-rounded'
                    onClick={() => {
                      setOpen(false);
                      setForceOpen(false);
                    }}
                  />
                </Stack>
                <Stack gap='24px'>
                  <FormControl>
                    <FormLabel>Primary Cloud</FormLabel>
                    <Select
                      value={demoSettings.primaryProvider}
                      onChange={(event) =>
                        updateDemoSettings({
                          ...demoSettings,
                          primaryProvider: event.target.value as
                            | Provider.Aws
                            | Provider.Azure
                            | Provider.Gcp,
                        })
                      }
                    >
                      {(
                        [Provider.Aws, Provider.Azure, Provider.Gcp] as const
                      ).map((provider) => (
                        <MuiMenuItem key={provider} value={provider}>
                          {CloudProviders[provider].displayName}
                        </MuiMenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Clouds</FormLabel>
                    {(
                      [
                        Provider.Aws,
                        Provider.Azure,
                        Provider.Gcp,
                        Provider.MongoAtlas,
                      ] as const
                    ).map((provider) => (
                      <FormControlLabel
                        className='my-[6px]'
                        key={provider}
                        control={<Switch className='mr-[12px]' />}
                        label={CloudProviders[provider].displayName}
                        checked={demoSettings[provider]}
                        onChange={(event, checked) => {
                          const updatedSettings = {
                            ...demoSettings,
                            [provider]: checked,
                          };

                          if (
                            !updatedSettings[updatedSettings.primaryProvider]
                          ) {
                            updatedSettings.primaryProvider =
                              (
                                [
                                  Provider.Aws,
                                  Provider.Azure,
                                  Provider.Gcp,
                                ] as const
                              ).find((x) => updatedSettings[x]) || Provider.Aws;
                          }

                          updateDemoSettings(updatedSettings);
                        }}
                      />
                    ))}
                  </FormControl>
                  <FormControl>
                    <FormLabel>Features</FormLabel>
                    {!assistantEnabled && (
                      <FormControlLabel
                        className='my-[6px]'
                        control={<Switch className='mr-[12px]' />}
                        label='AI'
                        checked={demoSettings.ai}
                        onChange={(event, checked) =>
                          updateDemoSettings({
                            ...demoSettings,
                            ai: checked,
                          })
                        }
                      />
                    )}
                    <FormControlLabel
                      className='my-[6px]'
                      control={<Switch className='mr-[12px]' />}
                      label='Log Space'
                      checked={demoSettings.logSpace}
                      onChange={(event, checked) =>
                        updateDemoSettings({
                          ...demoSettings,
                          logSpace: checked,
                        })
                      }
                    />
                    <FormControlLabel
                      className='my-[6px]'
                      control={<Switch className='mr-[12px]' />}
                      label='Ransomware'
                      checked={demoSettings.ransomware}
                      onChange={(event, checked) =>
                        updateDemoSettings({
                          ...demoSettings,
                          ransomware: checked,
                        })
                      }
                    />
                    <FormControlLabel
                      className='my-[6px]'
                      control={<Switch className='mr-[12px]' />}
                      label='Lakehouse'
                      checked={demoSettings.lakehouse}
                      onChange={(event, checked) =>
                        updateDemoSettings({
                          ...demoSettings,
                          lakehouse: checked,
                        })
                      }
                    />
                  </FormControl>
                  <Button
                    className='mt-[12px]'
                    variant='outlined'
                    onClick={() => window.location.reload()}
                  >
                    Reload
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Fade>
        )}
      </Popper>
    </>
  );
};
